// src/pages/blog/BlogCommentExpand.js
import React, { useEffect, useState, useCallback } from "react";

function formatKST(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

/**
 * 블로그 카드 하단에 붙는 "댓글(N) [댓글보기]" 토글 영역.
 * @param {object} props
 * @param {number} props.blogId
 * @param {number} props.initialCount  서버에서 받아온 댓글 수
 * @param {"light"|"dark"} props.theme  카드 배경 톤
 */
export default function BlogCommentExpand({ blogId, initialCount = 0, theme = "dark" }) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState(null); // null = 아직 불러오지 않음
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(initialCount || 0);

  useEffect(() => {
    setCount(initialCount || 0);
  }, [initialCount]);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`/api/blog/${blogId}/comments`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setComments(list);
        setCount(list.length);
      })
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, [blogId]);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!open && comments === null) {
      load();
    }
    setOpen((v) => !v);
  };

  const isLight = theme === "light";
  const wrapBorder = isLight ? "border-black/5" : "border-white/10";
  const countCls = isLight ? "text-slate-700" : "text-white/80";
  const btnCls = isLight
    ? "text-amber-700 hover:text-amber-900"
    : "text-amber-300 hover:text-amber-200";
  const subCls = isLight ? "text-slate-500" : "text-white/50";
  const titleCls = isLight ? "text-slate-900" : "text-white";
  const txtCls = isLight ? "text-slate-700" : "text-white/85";
  const itemBorder = isLight ? "border-black/5 bg-white" : "border-white/10 bg-white/5";

  return (
    <div className={`border-t ${wrapBorder} px-4 py-3`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between gap-3">
        <div className={`text-sm font-bold ${countCls}`}>
          💬 댓글 ({count})
        </div>
        <button
          type="button"
          onClick={handleToggle}
          className={`text-sm font-bold ${btnCls}`}
        >
          {open ? "댓글 접기 ▲" : "댓글보기 ▼"}
        </button>
      </div>

      {open && (
        <div className="mt-3 space-y-2">
          {loading ? (
            <div className={`text-sm ${subCls}`}>불러오는 중...</div>
          ) : !comments || comments.length === 0 ? (
            <div className={`text-sm ${subCls}`}>아직 댓글이 없습니다.</div>
          ) : (
            comments.map((c) => (
              <div key={c.id} className={`rounded-lg border px-3 py-2 ${itemBorder}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className={`text-xs font-bold ${titleCls}`}>{c.author_name}</div>
                  <div className={`text-[11px] ${subCls}`}>{formatKST(c.created_at)}</div>
                </div>
                <div className={`mt-1 text-sm whitespace-pre-wrap break-words ${txtCls}`}>
                  {c.content}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
